import {
  useCallback,
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

export type DragScrollSnapOptions = {
  /** Distance from the start of one item to the next (width + gap). */
  itemStride: number;
  /** How many items should land flush in view after settle. */
  visibleCount: number;
  disabled?: boolean;
};

function clearWindowSelection() {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) selection.removeAllRanges();
}

/**
 * Snap page from fractional index and recent pointer drag direction.
 * Positive direction = pointer dragged right → content moved left → earlier page (floor).
 * Negative = dragged left → later page (ceil). Near-zero → nearest page.
 */
export function snapPageFromDirection(
  rawIndex: number,
  pointerDirection: number,
  maxPage: number
): number {
  if (!Number.isFinite(rawIndex)) return 0;
  const clampedMax = Math.max(0, maxPage);
  if (!Number.isFinite(pointerDirection) || Math.abs(pointerDirection) < 1e-6) {
    return Math.max(0, Math.min(Math.round(rawIndex), clampedMax));
  }
  const page = pointerDirection > 0 ? Math.floor(rawIndex) : Math.ceil(rawIndex);
  return Math.max(0, Math.min(page, clampedMax));
}

/**
 * Pointer drag on an overflow-x scroller, settling to a page of `visibleCount`
 * items. On release mid-item, snaps in the content travel direction (opposite
 * of the recent pointer drag), not by which card is most visible.
 */
export function useDragScrollSnap<T extends HTMLElement>(
  options: DragScrollSnapOptions
): {
  ref: RefObject<T>;
  onPointerDown: (e: ReactPointerEvent<T>) => void;
  isDragging: () => boolean;
} {
  // visibleCount stays on the options type so callers size cards as
  // viewport/visibleCount; settle snaps by itemStride alone.
  const { itemStride, disabled } = options;
  const ref = useRef<T>(null);
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startScroll: number;
    moved: boolean;
    lastX: number;
    /** Most recent non-trivial pointer step (+ right / − left). */
    lastDelta: number;
  } | null>(null);
  const draggingRef = useRef(false);

  const settle = useCallback(
    (pointerDirection: number) => {
      const el = ref.current;
      if (!el || itemStride <= 0) return;
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      if (maxScroll <= 0) return;

      const maxPage = Math.max(0, Math.round(maxScroll / itemStride));
      const targetPage = snapPageFromDirection(
        el.scrollLeft / itemStride,
        pointerDirection,
        maxPage
      );
      el.scrollTo({ left: targetPage * itemStride, behavior: "smooth" });
    },
    [itemStride]
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<T>) => {
      if (disabled || e.button !== 0) return;
      const el = ref.current;
      if (!el) return;
      // Ignore interactive controls inside the strip.
      const target = e.target as HTMLElement | null;
      if (target?.closest("button, a, input, textarea, select, [role='button']")) return;

      // Drag should pan the strip only — never leave a stuck text highlight.
      clearWindowSelection();

      drag.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startScroll: el.scrollLeft,
        moved: false,
        lastX: e.clientX,
        lastDelta: 0,
      };
      draggingRef.current = false;
      el.setPointerCapture(e.pointerId);
    },
    [disabled]
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return undefined;

    const onMove = (e: PointerEvent) => {
      const state = drag.current;
      if (!state || e.pointerId !== state.pointerId) return;
      const dx = e.clientX - state.startX;
      if (!state.moved && Math.abs(dx) < 3) return;
      state.moved = true;
      draggingRef.current = true;
      const step = e.clientX - state.lastX;
      if (Math.abs(step) >= 1) state.lastDelta = step;
      state.lastX = e.clientX;
      clearWindowSelection();
      el.scrollLeft = state.startScroll - dx;
      e.preventDefault();
    };

    const onUp = (e: PointerEvent) => {
      const state = drag.current;
      if (!state || e.pointerId !== state.pointerId) return;
      drag.current = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      if (state.moved) {
        clearWindowSelection();
        const totalDx = e.clientX - state.startX;
        const direction = Math.abs(state.lastDelta) >= 1 ? state.lastDelta : totalDx;
        settle(direction);
        // Keep isDragging true briefly so click handlers on the strip can ignore.
        window.setTimeout(() => {
          draggingRef.current = false;
        }, 0);
      } else {
        draggingRef.current = false;
      }
    };

    el.addEventListener("pointermove", onMove, { passive: false });
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, [disabled, settle]);

  return {
    ref,
    onPointerDown,
    isDragging: () => draggingRef.current,
  };
}
